import {Component, OnInit, EventEmitter, Output, Input, SimpleChanges, OnChanges, ElementRef} from '@angular/core';
import {PositioningService} from '../positioning/positioning.service';

@Component({
  selector: 'hxa-datepicker',
  template: `<div class="hxui-reset">
  <div class="hx-card hxa-datepicker-container">
    <div class="hx-card-header hxa-datepicker-header">
      <div class="hxa-datepicker-month">
        <div class="hxa-datepicker-icon" title="Previous Month" (click)="previousMonth()">
            <a class="hx-button is-transparent"><i class="hx-icon icon-angle-left"></i></a>
        </div>
        <div class="hxa-datepicker-month-title">
          <span>{{viewDate.toLocaleString("en-au", { month: "long", year: "numeric" })}}</span></div>
        <div class="hxa-datepicker-icon" title="Next Month" (click)="nextMonth()">
            <a class="hx-button is-transparent"><i class="hx-icon icon-angle-right"></i></a>
        </div>
      </div>
      <div class="hxa-datepicker-week">
        <div class="hxa-datepicker-weekday" *ngFor="let weekday of week">
          {{weekday | slice:0:3}}
        </div>
      </div>
    </div>
    <div class="hxa-datepicker-contents">
      <div class="hxa-datepicker-day" *ngFor="let day of days" 
        [ngClass]="{'hxa-datepicker-day-siblingmonth': !isCurrentMonth(day), 
        'hxa-datepicker-day-currentday': isCurrentDay(day),
        'hxa-datepicker-day-selectedday': isSelectedDay(day),
        'hxa-datepicker-day-invalidday': isInvalidDay(day)}"
        (click)="setSelectedDate(day)">
      <a class="hx-button is-transparent">{{day.getDate()}}</a>
      </div>
    </div>
  </div>
</div>`,
  styles: [`.hxa-datepicker-container{max-width:21em;width:21em;height:24em;display:flex;flex-direction:column;font-size:1rem}.hxa-datepicker-header{padding:1rem 1rem 0;flex-direction:column;align-items:initial;justify-content:space-around;font-weight:100}.hxa-datepicker-icon{cursor:pointer;display:flex;flex:1;flex-direction:column;justify-content:center;align-items:center}.hxa-datepicker-icon .hx-button{position:initial!important;top:initial!important}.hxa-datepicker-icon .hx-button.is-transparent:hover{color:#000}.hxa-datepicker-icon .hx-icon{font-size:2.3em;position:initial!important;top:initial!important;color:#0d4d78}.hxa-datepicker-month{display:flex;text-align:center;margin-bottom:1rem}.hxa-datepicker-month-title{font-size:1.5em;flex:3;display:flex;justify-content:center;align-items:center;color:#0d4d78}.hxa-datepicker-week{display:flex;width:100%;text-align:center}.hxa-datepicker-weekday{flex:1;color:#0d4d78}.hxa-datepicker-contents{padding:1rem;background-color:rgba(246,246,249,.5);display:flex;flex-flow:row wrap;flex:1;justify-content:space-around;align-content:space-around}.hxa-datepicker-day{flex:1 1 14%;height:16.666%;display:flex;justify-content:center;align-items:center}.hxa-datepicker-day .hx-button{position:initial;top:initial;flex:1;font-weight:400;height:100%;padding:0;color:#41b987}.hxa-datepicker-day-siblingmonth .hx-button{color:#3b3b3b;font-weight:100}.hxa-datepicker-day-selectedday .hx-button{color:#fff;background:#41b987}.hxa-datepicker-day-invalidday .hx-button{color:#e0e0e1!important;pointer-events:none}.hxa-datepicker-day-currentday .hx-button{border:2px solid #41b987}`]
})
export class DatepickerComponent implements OnInit, OnChanges {
  @Input() public selectedDate: Date;
  @Input() public validators: Array<(date: Date) => boolean>;

  @Output() public onDateSelected: EventEmitter<Date> = new EventEmitter<Date>();

  public viewDate: Date;
  public days: Array<Date> = new Array<Date>();
  public week: Array<string> = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  private presentDate: Date;
  private cellCount = 41;

  constructor(
    private hostElement: ElementRef,
    private positioningService: PositioningService) { }

  // Populates the days array with the current month, and completes the view with partial dates from sibling months
  public renderCalendar(): void {
    for (let i = 0; i <= this.cellCount; i++) {
      // date will be set to the first day of the month set in this.viewDate
      const date: Date = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth());
      // Shifts the week to start from Monday, rather than Sunday, this causes the index to start at 1
      const dayOffset = date.getDay() === 0 ? 7 : date.getDay();
      this.days[i] = new Date(date.setDate(2 - dayOffset + i));
    }
  }

  // TODO: Maybe we should move all of this logic into the positioning service?
  private positionCalendar(): void {
    const rect = this.hostElement.nativeElement.getBoundingClientRect();
    const buffer = 10;
    if (this.positioningService.isElementBelowTheFold(this.hostElement.nativeElement)) {
      this.hostElement.nativeElement.style.top = (rect.top - (rect.top + rect.height + buffer)) + 'px';
    }
  }

  public previousMonth(): void {
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1);
    this.renderCalendar();
  }

  public nextMonth(): void {
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1);
    this.renderCalendar();
  }

  public isCurrentMonth(inputDate: Date): boolean {
    return inputDate.getMonth() === this.viewDate.getMonth();
  }

  public isCurrentDay(inputDate: Date): boolean {
    return inputDate.getTime() === this.presentDate.getTime();
  }

  public isSelectedDay(inputDate: Date): boolean {
    if (this.selectedDate) {
      return inputDate.getTime() === this.selectedDate.getTime();
    }

    return false;
  }

  public isInvalidDay(inputDate: Date): boolean {
    return this.validators.map((fn) => fn(inputDate)).reduce((prev, next) => prev || next, false);
  }

  public setSelectedDate(date: Date): void {
    if (!this.isInvalidDay(date)) {
      this.selectedDate = date;
      this.onDateSelected.emit(date);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!changes.selectedDate.currentValue) {
      this.viewDate = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth());
    }
  }

  ngOnInit(): void {
    const date: Date = new Date();
    this.presentDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    this.viewDate = this.viewDate || new Date(date.getFullYear(), date.getMonth());
    this.renderCalendar();
    this.positionCalendar();
  }
}
